if (!customElements.get('fairf-sample-slider')) {
  customElements.define('fairf-sample-slider', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const listen = (node, event, callback) => node.addEventListener(event, callback, { signal: this.abort.signal });
      const track = this.querySelector('[data-sample-track]');
      const cards = [...track.querySelectorAll('[data-sample-name]')];
      const groups = [...this.querySelectorAll('button[data-sample-group]')];
      const groupKey = value => ['grijs', 'zwart'].includes(value) ? 'grijs-zwart' : value === 'cr-234-me' ? 'cream' : value;
      let selectedGroup = groups.find(button => button.getAttribute('aria-pressed') === 'true')?.dataset.sampleGroup || '';
      const search = this.querySelector('[data-sample-search]');
      const collection = this.querySelector('[data-sample-collection]');
      const normalize = value => String(value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
      const collectionLabel = value => value === '24 tinten wit' ? (this.dataset.whiteCollection || value) : value;
      const params = new URL(location.href).searchParams;
      if (search && params.get('kleur')) search.value = params.get('kleur');
      const previous = this.querySelector('[data-sample-prev]');
      const next = this.querySelector('[data-sample-next]');
      const toggle = this.querySelector('[data-sample-toggle]');
      const collapse = this.querySelector('[data-sample-collapse]');
      const arrows = this.querySelector('[data-sample-arrows]');
      const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
      const update = () => {
        previous.disabled = track.scrollLeft < 2;
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
        arrows.hidden = this.classList.contains('is-expanded') || track.hidden || track.scrollWidth <= track.clientWidth + 2;
        if (!arrows.hidden) {
          const image = cards.find(card => !card.hidden)?.querySelector('.fairf-sample-slide__image');
          if (image) {
            const imageRect = image.getBoundingClientRect();
            arrows.style.top = `${imageRect.top - track.getBoundingClientRect().top + imageRect.height / 2}px`;
          }
        }
      };
      const move = direction => track.scrollBy({ left: direction * track.clientWidth * .85, behavior: reducedMotion() ? 'instant' : 'smooth' });
      const setExpanded = expanded => {
        this.classList.toggle('is-expanded', expanded);
        toggle.setAttribute('aria-expanded', String(expanded));
        collapse.setAttribute('aria-expanded', String(expanded));
        toggle.textContent = `${expanded ? toggle.dataset.collapseLabel : toggle.dataset.expandLabel} ${expanded ? '↑' : '↓'}`;
        collapse.hidden = !expanded;
        track.scrollLeft = 0;
        update();
        // The main toggle sits below the cards: keep the first row in view when the grid grows.
        this.querySelector('.fairf-sample-slider__heading').scrollIntoView({ block: 'start', behavior: 'instant' });
        (expanded ? collapse : toggle).focus({ preventScroll: true });
      };
      const filter = () => {
        let count = 0;
        const term = normalize(search?.value);
        const wanted = collection?.value || '';
        cards.forEach(card => {
          const matchesGroup = !selectedGroup || groupKey(card.dataset.sampleGroup) === selectedGroup;
          const matchesTerm = !term || normalize(card.dataset.sampleName).includes(term);
          const matchesCollection = !wanted || card.dataset.sampleCollection === wanted;
          card.hidden = !(matchesGroup && matchesTerm && matchesCollection);
          if (!card.hidden) count++;
        });
        if ((term || wanted) && !this.classList.contains('is-expanded') && count > 0) {
          this.classList.add('is-expanded'); toggle.setAttribute('aria-expanded', 'true'); collapse.setAttribute('aria-expanded', 'true');
          toggle.textContent = `${toggle.dataset.collapseLabel} ↑`; collapse.hidden = false;
        }
        this.querySelector('[data-sample-count]').textContent = `${count} ${count === 1 ? this.dataset.countOne : this.dataset.countLabel}`;
        this.querySelector('[data-sample-empty]').hidden = count > 0;
        track.hidden = count === 0;
        track.scrollLeft = 0;
        update();
      };
      groups.forEach(button => listen(button, 'click', () => {
        selectedGroup = button.dataset.sampleGroup;
        groups.forEach(item => item.setAttribute('aria-pressed', String(item === button)));
        filter();
      }));
      listen(toggle, 'click', () => setExpanded(!this.classList.contains('is-expanded')));
      listen(collapse, 'click', () => setExpanded(false));
      toggle.hidden = false;
      listen(previous, 'click', () => move(-1));
      listen(next, 'click', () => move(1));
      listen(track, 'scroll', update);
      listen(track, 'keydown', event => {
        if (this.classList.contains('is-expanded') || event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); move(event.key === 'ArrowLeft' ? -1 : 1);
      });
      this.resizeObserver = new ResizeObserver(update);
      this.resizeObserver.observe(track);
      if (search) listen(search, 'input', filter);
      if (collection) listen(collection, 'change', filter);
      // Collection tiles elsewhere on the page filter this block instead of a separate colour list.
      document.querySelectorAll('[data-collection-link]').forEach(link => listen(link, 'click', event => {
        if (!collection || ![...collection.options].some(option => option.value === link.dataset.collectionLink)) return;
        event.preventDefault();
        if (search) search.value = '';
        selectedGroup = ''; groups.forEach(item => item.setAttribute('aria-pressed', String(!item.dataset.sampleGroup)));
        collection.value = link.dataset.collectionLink;
        filter();
        this.querySelector('.fairf-sample-slider__heading').scrollIntoView({ behavior: reducedMotion() ? 'instant' : 'smooth' });
        collection.focus({ preventScroll: true });
      }));
      filter();
      if (this.dataset.colors && collection) {
        fetch(this.dataset.colors).then(r => r.json()).then(colors => {
          const byName = new Map(colors.map(color => [normalize(color.name), color.collection || '']));
          cards.forEach(card => { card.dataset.sampleCollection = byName.get(normalize(card.dataset.sampleName)) || ''; });
          [...new Set(cards.map(card => card.dataset.sampleCollection).filter(Boolean))].forEach(value => collection.add(new Option(collectionLabel(value), value)));
          const requested = params.get('collectie');
          if (requested && [...collection.options].some(option => option.value === requested)) collection.value = requested;
          filter();
        }).catch(() => { collection.closest('label').hidden = true; });
      }
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      this.resizeObserver?.disconnect();
    }
  });
}
